/**
 * ShareLinkCreateDialog — Phase-1 v15 rewrite of the "Create new share link"
 * flow. Replaces the inline 4-field form on the Shares tab with a full
 * modal offering every Phase-1 access-control lever: scope, identity gate,
 * expiry, download permission, max-downloads cap, NDA gate, session
 * timeout, and watermark.
 *
 * The dialog is dumb — it collects state, validates, and POSTs to
 * /api/data-room/shares. The parent (DataRoomPanel/SharesTab) refreshes
 * the shares list on success.
 *
 * Kept in its own file so DataRoomPanel.js stays under 1200 lines.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Loader2, Shield, RefreshCw, Copy, Info, Eye, Download,
  DownloadCloud, Ban, CalendarClock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EXPIRY_OPTIONS = [
  { value: 'never',  label: 'Never expires' },
  { value: '7',      label: '7 days' },
  { value: '14',     label: '14 days' },
  { value: '30',     label: '30 days' },
  { value: '60',     label: '60 days' },
  { value: '90',     label: '90 days' },
  { value: 'custom', label: 'Custom date…' },
];

const DOWNLOAD_OPTIONS = [
  { value: 'preview_download', label: 'Preview + Download', icon: <Download className="w-4 h-4" />, hint: 'Recipient can preview and download individual files.' },
  { value: 'preview_only',     label: 'Preview Only',        icon: <Eye className="w-4 h-4" />,        hint: 'Recipient can only preview in-browser. All downloads blocked.' },
  { value: 'download_all',     label: 'Download Everything', icon: <DownloadCloud className="w-4 h-4" />, hint: 'Recipient can bulk-download the entire room as a ZIP.' },
  { value: 'disabled',         label: 'Disable Downloads',   icon: <Ban className="w-4 h-4" />,        hint: 'Kill switch — preview allowed, no download button surfaces.' },
];

const SESSION_TIMEOUTS = [
  { value: 'none', label: 'No timeout' },
  { value: '15',   label: '15 minutes' },
  { value: '30',   label: '30 minutes' },
  { value: '120',  label: '2 hours' },
];

const ACCESS_SCOPES = [
  { value: 'entire',  label: 'Entire Company Data Room' },
  { value: 'asset',   label: 'Single Asset' },
  { value: 'assets',  label: 'Multiple Assets' },
  { value: 'folders', label: 'Selected Folders (by category)' },
];

const CATEGORY_OPTIONS = [
  { value: 'clinical',        label: 'Clinical' },
  { value: 'cmc',             label: 'CMC' },
  { value: 'patents',         label: 'Patents' },
  { value: 'manufacturing',   label: 'Manufacturing' },
  { value: 'publications',    label: 'Publications' },
  { value: 'regulatory',      label: 'Regulatory' },
  { value: 'presentations',   label: 'Presentations' },
  { value: 'misc',            label: 'Miscellaneous' },
];

function genLocalPassword() {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let out = '';
  const arr = new Uint32Array(10);
  crypto.getRandomValues(arr);
  for (let i = 0; i < 10; i++) out += alphabet[arr[i] % alphabet.length];
  return out;
}

export default function ShareLinkCreateDialog({
  open, onClose, biotechId, biotechName, assets, onCreated,
}) {
  // --- form state ---
  const [counterparties, setCounterparties] = useState([]);
  const [loadingCounterparties, setLoadingCounterparties] = useState(false);
  const [pharma, setPharma] = useState('');

  const [scope, setScope] = useState('entire');
  const [assetId, setAssetId] = useState('');
  const [assetIds, setAssetIds] = useState([]);
  const [folderCategories, setFolderCategories] = useState([]);

  const [identityRestricted, setIdentityRestricted] = useState(false);   // v15c — off by default
  const [identityMode, setIdentityMode] = useState('emails');   // 'emails' | 'domain'
  const [allowedEmails, setAllowedEmails] = useState('');
  const [allowedDomain, setAllowedDomain] = useState('');

  const [password, setPassword] = useState(genLocalPassword());
  const [passwordManual, setPasswordManual] = useState(false);

  const [expiryPreset, setExpiryPreset] = useState('30');
  const [customExpiryDate, setCustomExpiryDate] = useState('');

  const [downloadPermission, setDownloadPermission] = useState('preview_download');
  const [maxDownloads, setMaxDownloads] = useState('');
  const [ndaRequired, setNdaRequired] = useState(true);
  const [sessionTimeout, setSessionTimeout] = useState('30');
  const [watermarkEnabled, setWatermarkEnabled] = useState(false);
  const [note, setNote] = useState('');

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingCounterparties(true);
    axios
      .get(`${API}/data-room/pharma-counterparties?biotech_id=${biotechId}`, { withCredentials: true })
      .then(res => setCounterparties(res.data?.counterparties || []))
      .catch(() => setCounterparties([]))
      .finally(() => setLoadingCounterparties(false));
  }, [open, biotechId]);

  const expiresAtISO = useMemo(() => {
    if (expiryPreset === 'never') return null;
    if (expiryPreset === 'custom') {
      if (!customExpiryDate) return null;
      // Set expiry to end of chosen date (UTC).
      return new Date(customExpiryDate + 'T23:59:59Z').toISOString();
    }
    const days = parseInt(expiryPreset, 10);
    const d = new Date();
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString();
  }, [expiryPreset, customExpiryDate]);

  const submit = async () => {
    if (!pharma) return toast.error('Pick a pharma counterparty');
    // v15c — email/domain gate is now OPTIONAL. Only validate the
    // content of the identity-restriction block IF the user opted in.
    if (identityRestricted) {
      if (identityMode === 'emails' && !allowedEmails.trim()) {
        return toast.error('Add at least one recipient email — or turn off the identity restriction.');
      }
      if (identityMode === 'domain' && !allowedDomain.trim()) {
        return toast.error('Enter an allowed domain (e.g. faesfarma.com) — or turn off the identity restriction.');
      }
    }
    if (scope === 'asset' && !assetId) return toast.error('Pick an asset');
    if (scope === 'assets' && assetIds.length === 0) return toast.error('Pick at least one asset');
    if (scope === 'folders' && folderCategories.length === 0) return toast.error('Pick at least one folder');

    const payload = {
      biotech_id: biotechId,
      recipient_pharma_name: pharma,
      password: passwordManual ? password : password,
      scope,
      allowed_emails: (identityRestricted && identityMode === 'emails')
        ? allowedEmails.split(/[\n,]/).map(x => x.trim()).filter(Boolean)
        : [],
      allowed_domains: (identityRestricted && identityMode === 'domain')
        ? [allowedDomain.trim().replace(/^@/, '')]
        : [],
      expires_at: expiresAtISO,
      download_permission: downloadPermission,
      max_downloads_per_recipient: maxDownloads ? parseInt(maxDownloads, 10) : null,
      nda_required: ndaRequired,
      session_timeout_minutes: sessionTimeout === 'none' ? null : parseInt(sessionTimeout, 10),
      watermark_enabled: watermarkEnabled,
      note: note || undefined,
    };
    if (scope === 'asset') payload.asset_id = assetId;
    if (scope === 'assets') payload.asset_ids = assetIds;
    if (scope === 'folders') payload.folder_categories = folderCategories;

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/data-room/shares`, payload, { withCredentials: true });
      toast.success(`Share link created for ${pharma}`);
      onCreated?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create share');
    } finally {
      setSubmitting(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(password);
    toast.success('Password copied');
  };

  const noCounterparties = !loadingCounterparties && counterparties.length === 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="share-create-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Create secure Data Room share</DialogTitle>
        </DialogHeader>

        {noCounterparties ? (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
            <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              No pharma has a <strong>Finalised</strong> Buyer CDA on this biotech yet.
              Finalise a CDA in the Counterparty Engagement section first — the pharma will then appear here.
            </div>
          </div>
        ) : (
          <div className="space-y-6 pt-2">
            {/* SECTION 1 — Counterparty & Scope */}
            <SectionHeader>1 · Recipient &amp; scope</SectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Pharma counterparty" hint="Only pharmas with a Finalised Buyer CDA show here.">
                <Select value={pharma} onValueChange={setPharma}>
                  <SelectTrigger className="h-10" data-testid="share-pharma-select">
                    <SelectValue placeholder={loadingCounterparties ? 'Loading…' : 'Select pharma…'} />
                  </SelectTrigger>
                  <SelectContent>
                    {counterparties.map(c => (
                      <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Access scope" hint="What can the recipient see?">
                <Select value={scope} onValueChange={setScope}>
                  <SelectTrigger className="h-10" data-testid="share-scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCESS_SCOPES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {scope === 'asset' && (
                <Field label="Asset" className="md:col-span-2">
                  <Select value={assetId} onValueChange={setAssetId}>
                    <SelectTrigger className="h-10" data-testid="share-asset-select">
                      <SelectValue placeholder="Pick an asset…" />
                    </SelectTrigger>
                    <SelectContent>
                      {(assets || []).map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.asset_name || a.name || 'Unnamed'}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
              {scope === 'assets' && (
                <Field label="Assets" hint="Tick every asset the recipient should see." className="md:col-span-2">
                  <div className="border border-slate-200 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                    {(assets || []).map(a => (
                      <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={assetIds.includes(a.id)}
                          onCheckedChange={(v) => setAssetIds(prev => v ? [...prev, a.id] : prev.filter(x => x !== a.id))}
                          data-testid={`share-asset-check-${a.id}`}
                        />
                        {a.asset_name || a.name || 'Unnamed'}
                      </label>
                    ))}
                  </div>
                </Field>
              )}
              {scope === 'folders' && (
                <Field label="Folders" hint="Tick each category the recipient should see." className="md:col-span-2">
                  <div className="border border-slate-200 rounded-lg p-3 grid grid-cols-2 gap-2">
                    {CATEGORY_OPTIONS.map(c => (
                      <label key={c.value} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={folderCategories.includes(c.value)}
                          onCheckedChange={(v) => setFolderCategories(prev => v ? [...prev, c.value] : prev.filter(x => x !== c.value))}
                          data-testid={`share-folder-check-${c.value}`}
                        />
                        {c.label}
                      </label>
                    ))}
                  </div>
                </Field>
              )}
            </div>

            {/* SECTION 2 — Identity gate (OPTIONAL — v15c) */}
            <SectionHeader>2 · Who can access?</SectionHeader>
            <div className="border border-slate-200 rounded-lg p-4 space-y-3">
              <div className="text-[12px] text-slate-600 flex items-start gap-2">
                <Shield className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-emerald-600" />
                <span>
                  By default, anyone with the link + password can open this Data Room — no email verification,
                  no corporate SSO. Activity is logged at the link level (opens, previews, downloads).
                  This is how enterprise VDRs (Datasite, Firmex) work for external pharma reviewers.
                </span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="share-identity-toggle">
                <Checkbox checked={identityRestricted} onCheckedChange={setIdentityRestricted} />
                <span className="text-sm text-slate-700 font-medium">
                  Restrict to specific emails or a domain <span className="text-[11px] text-slate-400">(advanced, optional)</span>
                </span>
              </label>
              {identityRestricted && (
                <>
                  <div className="flex gap-3">
                    <RadioTile
                      active={identityMode === 'emails'}
                      onClick={() => setIdentityMode('emails')}
                      label="Specific emails"
                      hint="Whitelist exact addresses."
                      testId="identity-emails"
                    />
                    <RadioTile
                      active={identityMode === 'domain'}
                      onClick={() => setIdentityMode('domain')}
                      label="Entire domain"
                      hint="Anyone from @company.com."
                      testId="identity-domain"
                    />
                  </div>
                  {identityMode === 'emails' ? (
                    <Field label="Allowed emails" hint="One per line or comma-separated.">
                      <Textarea
                        value={allowedEmails}
                        onChange={e => setAllowedEmails(e.target.value)}
                        placeholder={'xavier@faesfarma.com\nmaria@faesfarma.com'}
                        className="min-h-[80px] text-sm"
                        data-testid="share-allowed-emails"
                      />
                    </Field>
                  ) : (
                    <Field label="Allowed domain" hint="Only the host part — no @ needed.">
                      <Input
                        value={allowedDomain}
                        onChange={e => setAllowedDomain(e.target.value)}
                        placeholder="faesfarma.com"
                        className="h-10"
                        data-testid="share-allowed-domain"
                      />
                    </Field>
                  )}
                </>
              )}
            </div>

            {/* SECTION 3 — Password */}
            <SectionHeader>3 · Password</SectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-end">
              <Field label={passwordManual ? 'Custom password' : 'Auto-generated password'}>
                <Input
                  value={password}
                  onChange={e => { setPassword(e.target.value); setPasswordManual(true); }}
                  className="h-10 font-mono text-sm tracking-wider"
                  data-testid="share-password-input"
                />
              </Field>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => { setPassword(genLocalPassword()); setPasswordManual(false); }}
                data-testid="share-password-regen"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> New
              </Button>
              <Button
                type="button" variant="outline" size="sm"
                onClick={copyPassword}
                data-testid="share-password-copy"
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
              </Button>
            </div>

            {/* SECTION 4 — Expiry */}
            <SectionHeader>4 · Expiry</SectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Link expires">
                <Select value={expiryPreset} onValueChange={setExpiryPreset}>
                  <SelectTrigger className="h-10" data-testid="share-expiry-preset">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRY_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {expiryPreset === 'custom' && (
                <Field label="Custom date">
                  <Input
                    type="date"
                    value={customExpiryDate}
                    onChange={e => setCustomExpiryDate(e.target.value)}
                    className="h-10"
                    data-testid="share-expiry-custom-date"
                  />
                </Field>
              )}
              {expiresAtISO && (
                <div className="md:col-span-2 text-[12px] text-slate-500 flex items-center gap-1.5">
                  <CalendarClock className="w-3.5 h-3.5" />
                  Link will auto-revoke on {new Date(expiresAtISO).toLocaleString()}
                </div>
              )}
            </div>

            {/* SECTION 5 — Download & watermark */}
            <SectionHeader>5 · Download &amp; protection</SectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {DOWNLOAD_OPTIONS.map(o => (
                <RadioTile
                  key={o.value}
                  active={downloadPermission === o.value}
                  onClick={() => setDownloadPermission(o.value)}
                  label={<span className="flex items-center gap-2">{o.icon} {o.label}</span>}
                  hint={o.hint}
                  testId={`share-dp-${o.value}`}
                />
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Max downloads (total)" hint="Cap across all recipients. Blank = unlimited.">
                <Input
                  type="number"
                  min="1"
                  value={maxDownloads}
                  onChange={e => setMaxDownloads(e.target.value)}
                  placeholder="Unlimited"
                  className="h-10"
                  data-testid="share-max-downloads"
                />
              </Field>
              <Field label="Session timeout">
                <Select value={sessionTimeout} onValueChange={setSessionTimeout}>
                  <SelectTrigger className="h-10" data-testid="share-session-timeout">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SESSION_TIMEOUTS.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Watermark (Phase 5)">
                <label className="flex items-center h-10 gap-2 text-sm cursor-pointer">
                  <Checkbox checked={watermarkEnabled} onCheckedChange={setWatermarkEnabled} data-testid="share-watermark" />
                  Stamp PDFs with viewer info
                </label>
              </Field>
            </div>

            {/* SECTION 6 — NDA & note */}
            <SectionHeader>6 · Confidentiality &amp; internal note</SectionHeader>
            <div className="border border-slate-200 rounded-lg p-4 space-y-3">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="share-nda-toggle">
                <Checkbox checked={ndaRequired} onCheckedChange={setNdaRequired} className="mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-slate-800">Require confidentiality acknowledgement</div>
                  <div className="text-[11px] text-slate-500">Recipient must tick &quot;I acknowledge this information is confidential&quot; before viewing any file. Acceptance is logged as an audit event.</div>
                </div>
              </label>
              <Field label="Internal note (admin-only)" hint="Not shown to the recipient.">
                <Input
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="e.g. Shared with BD lead Feb 12"
                  className="h-10"
                  data-testid="share-note"
                />
              </Field>
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="share-cancel-btn">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || noCounterparties}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="share-submit-btn"
          >
            {submitting ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Creating…</> : 'Create secure link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SectionHeader = ({ children }) => (
  <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 border-b border-slate-100 pb-1.5">
    {children}
  </div>
);

const Field = ({ label, hint, children, className = '' }) => (
  <div className={className}>
    <Label className="text-xs text-slate-600 font-medium mb-1 block">{label}</Label>
    {children}
    {hint && (
      <div className="text-[11px] text-slate-400 mt-1 flex items-start gap-1">
        <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
        <span>{hint}</span>
      </div>
    )}
  </div>
);

const RadioTile = ({ active, onClick, label, hint, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`flex-1 text-left p-3 rounded-lg border-2 transition-colors ${
      active ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white hover:border-slate-300'
    }`}
  >
    <div className={`text-sm font-medium ${active ? 'text-indigo-900' : 'text-slate-800'}`}>{label}</div>
    <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>
  </button>
);
